import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export function MaskedLine({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-8%" });
    return (
        <span ref={ref} className={`block overflow-hidden ${className}`}>
            <motion.span
                className="block will-change-transform"
                initial={{ y: "115%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export function FadeUp({
    children,
    delay = 0,
    className = "",
    y = 44,
    once = true,
}) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once, margin: "-10%" }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

export function SectionLabel({ index, title, className = "" }) {
    return (
        <FadeUp y={20} className={`flex items-center gap-4 ${className}`}>
            <span className="font-display text-xs font-medium tracking-[0.35em] text-gold">
                {index}
            </span>
            <span className="h-px w-12 bg-gold/50" />
            <span className="text-xs font-medium uppercase tracking-[0.4em] text-ash">
                {title}
            </span>
        </FadeUp>
    );
}

/**
 * Cinematic 3D reveal — perspective + rotate + scale entrance when scrolled into view.
 * Great for hero images, cards, and gallery tiles as they enter the viewport.
 */
export function Reveal3D({
    children,
    className = "",
    delay = 0,
    rotateX = 18,
    rotateY = -8,
    y = 60,
    scale = 0.9,
    perspective = 1400,
    once = true,
}) {
    return (
        <motion.div
            className={className}
            style={{
                transformPerspective: perspective,
                transformStyle: "preserve-3d",
                willChange: "transform, opacity",
            }}
            initial={{
                opacity: 0,
                y,
                scale,
                rotateX,
                rotateY,
            }}
            whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
                rotateX: 0,
                rotateY: 0,
            }}
            viewport={{ once, margin: "-8%" }}
            transition={{ duration: 1.2, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

/**
 * Lazy-loading image with cinematic 3D reveal.
 * - Native lazy loading (loading="lazy")
 * - Skeleton pulse while decoding
 * - 3D perspective + rotate + scale entrance via framer-motion when in view
 * - Onboard image-protection (draggable=false, contextMenu blocked)
 */
export function LazyImage3D({
    src,
    alt = "",
    className = "",
    imgClassName = "",
    delay = 0,
    rotateX = 16,
    rotateY = -6,
    scale = 0.9,
    y = 60,
    style,
    onLoadEnd,
    onClick,
    ...rest
}) {
    const [loaded, setLoaded] = useState(false);
    return (
        <motion.div
            className={`relative overflow-hidden ${className}`}
            style={{
                transformPerspective: 1400,
                transformStyle: "preserve-3d",
                willChange: "transform, opacity",
            }}
            initial={{
                opacity: 0,
                y,
                scale,
                rotateX,
                rotateY,
            }}
            whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
                rotateX: 0,
                rotateY: 0,
            }}
            viewport={{ once: true, margin: "-6%" }}
            transition={{ duration: 1.15, delay, ease: EASE }}
            onClick={onClick}
        >
            {/* Skeleton shimmer while the image decodes */}
            {!loaded && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 animate-pulse bg-gradient-to-br from-graphite via-onyx to-graphite"
                />
            )}
            <motion.img
                src={src}
                alt={alt}
                loading="lazy"
                decoding="async"
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
                onLoad={() => {
                    setLoaded(true);
                    onLoadEnd?.();
                }}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{
                    opacity: loaded ? 1 : 0,
                    scale: loaded ? 1 : 1.06,
                }}
                transition={{ duration: 0.9, ease: EASE }}
                style={style}
                className={`h-full w-full object-cover ${imgClassName}`}
                {...rest}
            />
        </motion.div>
    );
}
